// components/modals/MeetingEventFormModal.js

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import toast from 'react-hot-toast';
import { FiUpload, FiTrash2, FiLoader, FiFileText, FiX } from 'react-icons/fi';

import * as meetingsApi from '../../services/meetingsApi';
const modalRoot = document.getElementById('modal-root');

const MeetingEventFormModal = ({
  isOpen,
  onClose,
  onSave,
  meetingId,
  event,
  accessToken,
}) => {
  const [newVttFile, setNewVttFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const vttInputRef = useRef(null);

  // Overflow lock
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';
    return () => (document.body.style.overflow = 'unset');
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith('.vtt')) {
      setNewVttFile(file);
    } else if (file) {
      toast.error('Invalid file type. Please upload a .vtt file.');
      e.target.value = null;
    }
  };

  const handleAddTranscript = async () => {
    if (!newVttFile) {
      toast.error('Please select a .vtt file to upload.');
      return;
    }
    setIsUploading(true);
    try {
      // +++ Use the real API +++
      const updatedMeeting = await meetingsApi.addTranscript(
        accessToken,
        meetingId,
        event.id,
        newVttFile,
      );
      onSave(updatedMeeting); // Pass the whole updated meeting up

      toast.success('Transcript added, event is reprocessing.');
      setNewVttFile(null);
      if (vttInputRef.current) vttInputRef.current.value = null;
    } catch (err) {
      toast.error(`Failed to add transcript: ${err.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteTranscript = async (transcriptId) => {
    if (!window.confirm('Are you sure you want to delete this transcript?'))
      return;

    try {
      // +++ Use the real API +++
      const updatedMeeting = await meetingsApi.deleteTranscript(
        accessToken,
        meetingId,
        event.id,
        transcriptId,
      );
      onSave(updatedMeeting); // Pass updated meeting up
      toast.success('Transcript deleted.');
    } catch (err) {
      toast.error(`Failed to delete transcript: ${err.message}`);
    }
  };

  return ReactDOM.createPortal(
    // eslint-disable-next-line
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* eslint-disable-next-line */}
      <div
        className="text-p-text relative flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl bg-zinc-900 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-700 p-4 md:p-6">
          <div>
            <h3 className="text-xl font-semibold text-white">
              Manage Event Transcripts
            </h3>
            <p className="text-sm text-blue-300">
              Event Date: {new Date(event.date).toLocaleDateString()}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-gray-400 hover:bg-slate-700 hover:text-white"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 overflow-y-auto p-4 md:p-6">
          {/* Add New Transcript Form */}
          {/* eslint-disable-next-line */}
          <div className="space-y-3 rounded-lg border border-slate-700 bg-slate-800 p-3">
            <label
              htmlFor="vtt-transcript"
              className="text-xs font-medium text-slate-400"
            >
              Add New VTT Transcript
            </label>
            <input
              type="file"
              ref={vttInputRef}
              accept=".vtt"
              onChange={handleFileChange}
              className="w-full text-sm text-slate-300
                                file:mr-4 file:rounded-md file:border-0 file:bg-slate-700 file:px-4
                                file:py-2 file:text-xs file:font-semibold file:text-slate-300
                                hover:file:bg-slate-600"
            />
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleAddTranscript}
                disabled={!newVttFile || isUploading}
                className="flex h-8 w-36 items-center justify-center gap-2 rounded-md bg-green-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-green-700 disabled:bg-slate-600"
              >
                {isUploading ? (
                  <FiLoader className="animate-spin" />
                ) : (
                  <>
                    <FiUpload size={14} /> Add Transcript
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Existing Transcripts List */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-slate-300">
              Existing Transcripts ({event.transcripts.length})
            </h4>
            {event.transcripts.length > 0 ? (
              event.transcripts.map((t) => (
                <div
                  key={t.id}
                  className="flex items-center justify-between rounded-md bg-slate-800 p-2"
                >
                  <div className="flex items-center gap-3">
                    <FiFileText className="text-blue-400" />
                    <p className="text-sm text-slate-200">{t.filename}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteTranscript(t.id)}
                    title="Delete Transcript"
                    className="rounded-full p-1 text-slate-400 hover:bg-slate-700 hover:text-red-500"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              ))
            ) : (
              <p className="py-2 text-center text-sm text-slate-500">
                No transcripts for this event.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>,
    modalRoot,
  );
};

export default MeetingEventFormModal;
